import { ArrowUpRight, Activity, Wallet, ChartLine, ShoppingBag, Users, Star } from "lucide-react";

/* SVG-based realistic mockup tiles — light theme */

const DashboardMock = () => (
  <div className="absolute inset-0 p-5 flex flex-col">
    <div className="flex items-center justify-between text-[10px] text-[#6b7280] font-mono-d">
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-[#ff5e57]" />
        <span className="w-2 h-2 rounded-full bg-[#ffbe3b]" />
        <span className="w-2 h-2 rounded-full bg-[#10b981]" />
        <span className="ml-2">analytics.arj.dev</span>
      </div>
      <span>v2.4</span>
    </div>
    <div className="mt-4 grid grid-cols-3 gap-2">
      {[
        { l: "Users", v: "284k", c: ChartLine },
        { l: "Revenue", v: "$1.2M", c: Wallet },
        { l: "Active", v: "+24%", c: Activity },
      ].map((m) => {
        const Icon = m.c;
        return (
          <div key={m.l} className="rounded-lg bg-white border border-[#e6ecff] p-2.5 shadow-sm">
            <Icon className="w-3.5 h-3.5 text-[#0066ff]" />
            <div className="text-[9px] text-[#6b7280] mt-1.5 uppercase tracking-wider">{m.l}</div>
            <div className="font-display text-sm font-bold text-[#0a0e1a]">{m.v}</div>
          </div>
        );
      })}
    </div>
    <div className="mt-3 flex-1 rounded-lg bg-white border border-[#e6ecff] p-3">
      <svg viewBox="0 0 200 80" className="w-full h-full">
        <defs>
          <linearGradient id="gradLight" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#0066ff" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#0066ff" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d="M0,60 C30,40 50,30 80,42 C110,54 140,18 200,28 L200,80 L0,80 Z" fill="url(#gradLight)" />
        <path d="M0,60 C30,40 50,30 80,42 C110,54 140,18 200,28" fill="none" stroke="#0066ff" strokeWidth="2" />
      </svg>
    </div>
  </div>
);

const MobileMock = () => (
  <div className="absolute inset-0 p-5 flex items-center justify-center">
    <div className="w-[140px] h-full rounded-[24px] bg-white border border-[#e6ecff] p-3 shadow-[0_22px_60px_-25px_rgba(0,102,255,0.35)]">
      <div className="flex items-center justify-between text-[8px] text-[#6b7280] font-mono-d mb-3">
        <span>9:41</span>
        <span>5G</span>
      </div>
      <div className="text-[9px] text-[#6b7280]">Hi, Aarav</div>
      <div className="font-display text-xs font-bold text-[#0a0e1a]">Wallet</div>
      <div className="mt-2 rounded-xl p-2.5 bg-gradient-to-br from-[#0066ff] to-[#0088ff] text-white">
        <div className="text-[8px] uppercase tracking-wider opacity-80">Balance</div>
        <div className="font-display text-sm font-bold">₹ 84,920</div>
      </div>
      <div className="mt-2.5 grid grid-cols-3 gap-1.5">
        {["Send", "Pay", "Top"].map((t) => (
          <div key={t} className="text-[8px] text-center py-1.5 rounded-md bg-[#f4f6fa] text-[#1f2937]">{t}</div>
        ))}
      </div>
      <div className="mt-2.5 space-y-1.5">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center justify-between text-[8px]">
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 rounded-full bg-gradient-to-br from-[#0066ff] to-[#10b981]" />
              <span className="text-[#1f2937]">Payment {i}</span>
            </div>
            <span className="text-[#10b981] font-mono-d font-semibold">+₹{i * 280}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const EcomMock = () => (
  <div className="absolute inset-0 p-5 flex flex-col">
    <div className="flex items-center justify-between">
      <div className="inline-flex items-center gap-2 text-[10px] text-[#1f2937] font-mono-d">
        <ShoppingBag className="w-3.5 h-3.5 text-[#0066ff]" />
        shop.brand.io
      </div>
      <div className="text-[10px] text-[#10b981] font-mono-d font-semibold">ORDERS +18%</div>
    </div>
    <div className="mt-3 flex-1 rounded-xl border border-[#e6ecff] bg-white p-3 relative overflow-hidden">
      <div className="text-[9px] uppercase tracking-wider text-[#0066ff] font-mono-d">Best Sellers</div>
      <div className="font-display text-base font-bold text-[#0a0e1a]">Spring Drop</div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        {[
          { l: "Sneakers", v: "284" },
          { l: "Tees", v: "162" },
          { l: "Caps", v: "94" },
          { l: "Bags", v: "58" },
        ].map((s) => (
          <div key={s.l} className="rounded-lg bg-[#f7f9fc] border border-[#e6ecff] p-2">
            <div className="text-[8px] uppercase text-[#6b7280] font-mono-d">{s.l}</div>
            <div className="font-display text-sm font-bold text-[#0a0e1a]">{s.v}</div>
          </div>
        ))}
      </div>
    </div>
    <button className="mt-3 py-2 rounded-lg btn-primary !text-[11px] !py-2 justify-center">
      View Storefront
    </button>
  </div>
);

const items = [
  { id: "fin", title: "FinPulse — Analytics Platform", tag: "Web App",    body: "Real-time metrics serving 280k+ users with sub-second queries.",      Mock: DashboardMock },
  { id: "wal", title: "Spendly — Mobile Wallet",       tag: "Mobile App", body: "Gen-Z banking with instant UPI, smart insights and offline sync.",    Mock: MobileMock   },
  { id: "shop", title: "Drift — D2C Storefront",       tag: "E-Commerce", body: "Headless commerce with same-day checkout and AI recommendations.",    Mock: EcomMock     },
];

export default function Portfolio() {
  return (
    <section id="portfolio" data-testid="portfolio-section" className="relative py-20 sm:py-28">
      <div className="absolute inset-0 bg-dots opacity-60 pointer-events-none" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex items-end justify-between gap-6 mb-10 reveal">
          <div>
            <div className="text-xs font-mono-d uppercase tracking-[0.28em] text-[#0066ff] mb-3">
              / 04 — Selected Work
            </div>
            <h2 className="font-display text-3xl sm:text-5xl font-bold leading-tight text-[#0a0e1a]">
              Real products. <span className="text-grad">Shipped.</span>
            </h2>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-xs text-[#6b7280]">
            <Users className="w-4 h-4 text-[#10b981]" />
            <span className="font-mono-d">120+ teams trust ARJ</span>
            <Star className="w-4 h-4 text-[#0066ff] ml-2" />
            <span className="font-mono-d">4.9 / 5.0</span>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {items.map((item, idx) => {
            const Mock = item.Mock;
            return (
              <article
                key={item.id}
                data-testid={`portfolio-card-${item.id}`}
                className="reveal group relative rounded-2xl overflow-hidden border border-[#e6ecff] bg-white tilt"
                style={{ transitionDelay: `${idx * 100}ms` }}
              >
                <div className="relative h-72 sm:h-80 bg-paper-soft border-b border-[#eef0f5]">
                  <div className="absolute inset-0 bg-lattice opacity-50" />
                  <Mock />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] uppercase tracking-[0.2em] font-mono-d text-[#10b981]">
                      {item.tag}
                    </span>
                    <ArrowUpRight className="w-4 h-4 text-[#94a3b8] group-hover:text-[#0066ff] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-[#0a0e1a]">{item.title}</h3>
                  <p className="mt-1.5 text-sm text-[#475569] leading-relaxed">{item.body}</p>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
